import { useState, useEffect, useRef } from 'react';
import { api } from '../utils/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Alert, AlertDescription } from './ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Separator } from './ui/separator';
import { User, Mail, Building, Save, GraduationCap, BookOpen, Lock, Eye, EyeOff, CheckCircle2, Shield, Pencil, X, KeyRound, IdCard, ChevronsUpDown, Check, Briefcase, Camera, Loader2 } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { cn } from './ui/utils';
import { User as UserType } from '../types';
import { useAuth } from '../contexts/AuthContext';

interface ProfileManagerProps {
  user: UserType;
  onUpdateProfile: (updatedUser: UserType) => void;
}

const ACADEMIC_RANKS = [
  'Instructor I', 'Instructor II', 'Instructor III',
  'Assistant Professor I', 'Assistant Professor II', 'Assistant Professor III', 'Assistant Professor IV',
  'Associate Professor I', 'Associate Professor II', 'Associate Professor III', 'Associate Professor IV', 'Associate Professor V',
  'Professor I', 'Professor II', 'Professor III', 'Professor IV', 'Professor V', 'Professor VI',
  'University Professor',
  'Visiting Professor',
];

export function ProfileManager({ user, onUpdateProfile }: ProfileManagerProps) {
  const { updateProfile, changePassword, uploadAvatar } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('profile');
  const [positionOpen, setPositionOpen] = useState(false);
  const [departmentOpen, setDepartmentOpen] = useState(false);
  const [programOptions, setProgramOptions] = useState<{ id: string; code: string; name: string }[]>([]);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState('');
  const avatarInputRef = useRef<HTMLInputElement>(null);

  const MAX_AVATAR_SIZE = 5 * 1024 * 1024; // 5MB
  const ALLOWED_AVATAR_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarError('');

    if (!ALLOWED_AVATAR_TYPES.includes(file.type)) {
      setAvatarError('Please upload a JPG, PNG, GIF, or WebP image.');
      return;
    }
    if (file.size > MAX_AVATAR_SIZE) {
      setAvatarError('Image must be smaller than 5MB.');
      return;
    }

    setAvatarUploading(true);
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      await uploadAvatar(base64, file.name);
    } catch (err: any) {
      setAvatarError(err.message || 'Failed to upload avatar');
    } finally {
      setAvatarUploading(false);
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const res = await api.getPublicCourses();
        setProgramOptions((res.courses || []).map((c: any) => ({ id: c.id, code: c.code, name: c.name })));
      } catch (err) {
        console.error('Failed to load programs:', err);
      }
    };
    if (user.role === 'faculty' || user.role === 'administrator') {
      fetchPrograms();
    }
  }, [user.role]);
  const [formData, setFormData] = useState({
    name: user.name,
    department: user.department || '',
    yearLevel: user.yearLevel || undefined,
    course: user.course || '',
    position: user.position || ''
  });

  // Password change state
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      await updateProfile(formData);
      setIsEditing(false);
    } catch (err: any) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: user.name,
      department: user.department || '',
      yearLevel: user.yearLevel || undefined,
      course: user.course || '',
      position: user.position || ''
    });
    setIsEditing(false);
    setError('');
    setPositionOpen(false);
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (passwordData.newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters long');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (passwordData.currentPassword === passwordData.newPassword) {
      setPasswordError('New password must be different from the current password');
      return;
    }

    setPasswordLoading(true);
    try {
      await changePassword(passwordData.currentPassword, passwordData.newPassword);
      setPasswordSuccess('Password updated successfully!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowCurrentPassword(false);
      setShowNewPassword(false);
      setShowConfirmPassword(false);
      setTimeout(() => {
        setPasswordSuccess('');
      }, 3000);
    } catch (err: any) {
      setPasswordError(err.message || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handlePasswordCancel = () => {
    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setPasswordError('');
    setPasswordSuccess('');
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  const getRoleDisplayName = (role: string) => {
    switch (role) {
      case 'administrator':
        return 'Administrator';
      case 'student':
        return 'Student';
      case 'faculty':
        return 'Faculty';
      default:
        return role;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'administrator':
        return { bg: 'from-red-500 to-rose-600', ring: 'ring-red-200', badge: 'bg-red-100 text-red-700 border-red-200', iconBg: 'bg-red-50', iconColor: 'text-red-600' };
      case 'faculty':
        return { bg: 'from-blue-500 to-indigo-600', ring: 'ring-blue-200', badge: 'bg-blue-100 text-blue-700 border-blue-200', iconBg: 'bg-blue-50', iconColor: 'text-blue-600' };
      case 'student':
        return { bg: 'from-emerald-500 to-teal-600', ring: 'ring-emerald-200', badge: 'bg-emerald-100 text-emerald-700 border-emerald-200', iconBg: 'bg-emerald-50', iconColor: 'text-emerald-600' };
      default:
        return { bg: 'from-gray-500 to-slate-600', ring: 'ring-gray-200', badge: 'bg-gray-100 text-gray-700 border-gray-200', iconBg: 'bg-gray-50', iconColor: 'text-gray-600' };
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const roleColors = getRoleColor(user.role);

  return (
    <div className="w-full max-w-full space-y-5 overflow-x-hidden">
      {/* Profile Header Banner */}
      <div className={`relative overflow-hidden rounded-xl bg-gradient-to-r ${roleColors.bg} p-5 sm:p-6`}>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2220%22%20height%3D%2220%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Ccircle%20cx%3D%221%22%20cy%3D%221%22%20r%3D%221%22%20fill%3D%22rgba(255%2C255%2C255%2C0.08)%22/%3E%3C/svg%3E')] opacity-60" />
        <div className="relative flex flex-col sm:flex-row items-center gap-4 sm:gap-5">
          <div className="relative group flex-shrink-0">
            <Avatar className={`w-16 h-16 sm:w-20 sm:h-20 ring-4 ring-white/30 shadow-xl`}>
              {user.avatarUrl && <AvatarImage src={user.avatarUrl} alt={user.name} />}
              <AvatarFallback className={`text-lg sm:text-xl font-bold bg-white/20 text-white backdrop-blur-sm`}>
                {getInitials(user.name)}
              </AvatarFallback>
            </Avatar>
            <button
              type="button"
              onClick={() => avatarInputRef.current?.click()}
              disabled={avatarUploading}
              className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer disabled:cursor-wait"
              aria-label="Change profile picture"
            >
              {avatarUploading ? (
                <Loader2 className="w-5 h-5 text-white animate-spin" />
              ) : (
                <Camera className="w-5 h-5 text-white" />
              )}
            </button>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              className="hidden"
              onChange={handleAvatarChange}
            />
          </div>
          <div className="flex-1 text-center sm:text-left min-w-0">
            <h2 className="text-lg sm:text-xl font-bold text-white truncate">{user.name}</h2>
            {avatarError && (
              <p className="text-xs text-red-200 mt-0.5">{avatarError}</p>
            )}
            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 mt-1.5">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-white/20 text-white backdrop-blur-sm border border-white/20">
                <Shield className="w-3 h-3" />
                {getRoleDisplayName(user.role)}
              </span>
              {user.department && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-white/15 text-white/90 backdrop-blur-sm border border-white/15">
                  <Building className="w-3 h-3" />
                  {user.department}
                </span>
              )}
            </div>
            <p className="text-xs text-white/70 mt-1.5 truncate">{user.email}</p>
          </div>
          {!isEditing && (
            <Button 
              onClick={() => setIsEditing(true)} 
              size="sm" 
              variant="secondary" 
              className="w-full sm:w-auto flex-shrink-0 bg-white/20 hover:bg-white/30 text-white border-white/20 backdrop-blur-sm shadow-lg"
            >
              <Pencil className="mr-1.5 h-3.5 w-3.5" />
              Edit Profile
            </Button>
          )}
        </div>
      </div>

      {/* Tabs for Profile / Security */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full max-w-full">
        <TabsList className="w-full grid grid-cols-2 h-auto min-h-11 p-1 bg-muted/60 rounded-lg">
          <TabsTrigger 
            value="profile" 
            className="flex items-center justify-center gap-2 px-2 py-2 text-sm font-medium data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-md transition-all"
          >
            <User className="h-4 w-4" />
            <span className="hidden xs:inline">Profile</span>
            <span className="xs:hidden">Profile</span>
          </TabsTrigger>
          <TabsTrigger 
            value="security" 
            className="flex items-center justify-center gap-2 px-2 py-2 text-sm font-medium data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-md transition-all"
          >
            <KeyRound className="h-4 w-4" />
            <span className="hidden xs:inline">Security</span>
            <span className="xs:hidden">Security</span>
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile" className="mt-4">
          <Card className="border shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <div className={`p-1.5 rounded-lg ${roleColors.iconBg}`}>
                  <IdCard className={`h-4 w-4 ${roleColors.iconColor}`} />
                </div>
                {isEditing ? 'Edit Profile Information' : 'Profile Details'}
              </CardTitle>
              <CardDescription className="text-xs">
                {isEditing ? 'Update your personal details below' : 'Your personal information and account details'}
              </CardDescription>
            </CardHeader>
            <Separator />
            <CardContent className="pt-5">
              {isEditing ? (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="name" className="text-sm font-medium flex items-center gap-2">
                      <User className="h-3.5 w-3.5 text-muted-foreground" />
                      Full Name
                    </Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      className="h-10 transition-colors focus:border-blue-400"
                    />
                  </div>
                  
                  {(user.role === 'faculty' || user.role === 'administrator') && (
                    <div className="space-y-1.5">
                      <Label className="text-sm font-medium flex items-center gap-2">
                        <Building className="h-3.5 w-3.5 text-muted-foreground" />
                        Program
                      </Label>
                      <Popover open={departmentOpen} onOpenChange={setDepartmentOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={departmentOpen}
                            className="w-full justify-between font-normal h-10"
                            type="button"
                          >
                            {formData.department || 'Select program...'}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                          <Command>
                            <CommandInput placeholder="Search program..." />
                            <CommandList>
                              <CommandEmpty>No program found.</CommandEmpty>
                              <CommandGroup>
                                {programOptions.map((program) => (
                                  <CommandItem
                                    key={program.id}
                                    value={`${program.code} - ${program.name}`}
                                    onSelect={(value) => {
                                      setFormData({ ...formData, department: value });
                                      setDepartmentOpen(false);
                                    }}
                                  >
                                    <Check className={cn('mr-2 h-4 w-4', formData.department === `${program.code} - ${program.name}` ? 'opacity-100' : 'opacity-0')} />
                                    {program.code} - {program.name}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>
                  )}

                  {user.role === 'faculty' && (
                    <div className="space-y-1.5">
                      <Label className="text-sm font-medium flex items-center gap-2">
                        <Briefcase className="h-3.5 w-3.5 text-muted-foreground" />
                        Academic Rank | Position
                      </Label>
                      <Popover open={positionOpen} onOpenChange={setPositionOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={positionOpen}
                            className="w-full justify-between font-normal h-10"
                            type="button"
                          >
                            {formData.position || 'Select academic rank...'}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                          <Command>
                            <CommandInput placeholder="Search academic rank..." />
                            <CommandList>
                              <CommandEmpty>No rank found.</CommandEmpty>
                              <CommandGroup>
                                {ACADEMIC_RANKS.map((rank) => (
                                  <CommandItem
                                    key={rank}
                                    value={rank}
                                    onSelect={(value) => {
                                      setFormData({ ...formData, position: value });
                                      setPositionOpen(false);
                                    }}
                                  >
                                    <Check className={cn('mr-2 h-4 w-4', formData.position === rank ? 'opacity-100' : 'opacity-0')} />
                                    {rank}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>
                  )}
                  
                  {user.role === 'student' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label htmlFor="course" className="text-sm font-medium flex items-center gap-2">
                          <BookOpen className="h-3.5 w-3.5 text-muted-foreground" />
                          Course
                        </Label>
                        <Input
                          id="course"
                          value={formData.course}
                          onChange={(e) => setFormData({...formData, course: e.target.value})}
                          placeholder="e.g., MAED, EdD-EM"
                          className="h-10 transition-colors focus:border-blue-400"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="yearLevel" className="text-sm font-medium flex items-center gap-2">
                          <GraduationCap className="h-3.5 w-3.5 text-muted-foreground" />
                          Year Level
                        </Label>
                        <Input
                          id="yearLevel"
                          type="number"
                          min="1"
                          max="3"
                          value={formData.yearLevel || ''}
                          onChange={(e) => setFormData({...formData, yearLevel: parseInt(e.target.value) || undefined})}
                          placeholder="1-3"
                          className="h-10 transition-colors focus:border-blue-400"
                        />
                      </div>
                    </div>
                  )}
                  
                  {error && (
                    <Alert variant="destructive" className="animate-in fade-in slide-in-from-top-1">
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}
                  
                  <Separator className="my-2" />
                  <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                    <Button type="button" variant="outline" onClick={handleCancel} className="w-full sm:w-auto" disabled={isLoading}>
                      <X className="mr-1.5 h-3.5 w-3.5" />
                      Cancel
                    </Button>
                    <Button type="submit" className={`w-full sm:w-auto bg-gradient-to-r ${roleColors.bg} hover:opacity-90 text-white shadow-md`} disabled={isLoading}>
                      <Save className="mr-1.5 h-3.5 w-3.5" />
                      {isLoading ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="space-y-1">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {/* ID Number */}
                    <div className="group flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors">
                      <div className={`p-2 rounded-lg ${roleColors.iconBg} flex-shrink-0 group-hover:scale-105 transition-transform`}>
                        <User className={`w-4 h-4 ${roleColors.iconColor}`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">ID Number</p>
                        <p className="text-sm font-semibold text-foreground truncate">{user.idNumber}</p>
                      </div>
                    </div>
                    
                    {/* Email */}
                    <div className="group flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors">
                      <div className={`p-2 rounded-lg ${roleColors.iconBg} flex-shrink-0 group-hover:scale-105 transition-transform`}>
                        <Mail className={`w-4 h-4 ${roleColors.iconColor}`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Email</p>
                        <p className="text-sm font-semibold text-foreground truncate">{user.email}</p>
                      </div>
                    </div>
                    
                    {/* Department */}
                    {user.department && (
                      <div className="group flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors">
                        <div className={`p-2 rounded-lg ${roleColors.iconBg} flex-shrink-0 group-hover:scale-105 transition-transform`}>
                          <Building className={`w-4 h-4 ${roleColors.iconColor}`} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Department</p>
                          <p className="text-sm font-semibold text-foreground break-words">{user.department}</p>
                        </div>
                      </div>
                    )}

                    {/* Academic Rank (Faculty) */}
                    {user.role === 'faculty' && user.position && (
                      <div className="group flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors">
                        <div className={`p-2 rounded-lg ${roleColors.iconBg} flex-shrink-0 group-hover:scale-105 transition-transform`}>
                          <Briefcase className={`w-4 h-4 ${roleColors.iconColor}`} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Academic Rank | Position</p>
                          <p className="text-sm font-semibold text-foreground break-words">{user.position}</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Course (Student) */}
                    {user.role === 'student' && user.course && (
                      <div className="group flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors">
                        <div className={`p-2 rounded-lg ${roleColors.iconBg} flex-shrink-0 group-hover:scale-105 transition-transform`}>
                          <BookOpen className={`w-4 h-4 ${roleColors.iconColor}`} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Course</p>
                          <p className="text-sm font-semibold text-foreground break-words">{user.course}</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Year Level (Student) */}
                    {user.role === 'student' && user.yearLevel && (
                      <div className="group flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors">
                        <div className={`p-2 rounded-lg ${roleColors.iconBg} flex-shrink-0 group-hover:scale-105 transition-transform`}>
                          <GraduationCap className={`w-4 h-4 ${roleColors.iconColor}`} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Year Level</p>
                          <p className="text-sm font-semibold text-foreground">
                            {
                              user.yearLevel === 1 ? '1st Year' :
                              user.yearLevel === 2 ? '2nd Year' :
                              user.yearLevel === 3 ? '3rd Year' :
                              `Year ${user.yearLevel || 'N/A'}`
                            }
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Role */}
                    <div className="group flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors">
                      <div className={`p-2 rounded-lg ${roleColors.iconBg} flex-shrink-0 group-hover:scale-105 transition-transform`}>
                        <Shield className={`w-4 h-4 ${roleColors.iconColor}`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Role</p>
                        <Badge className={`mt-0.5 text-xs font-semibold border ${roleColors.badge}`}>
                          {getRoleDisplayName(user.role)}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security" className="mt-4">
          <Card className="border shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <div className={`p-1.5 rounded-lg ${roleColors.iconBg}`}>
                  <Lock className={`h-4 w-4 ${roleColors.iconColor}`} />
                </div>
                Change Password
              </CardTitle>
              <CardDescription className="text-xs">
                Update your account password to keep your account secure
              </CardDescription>
            </CardHeader>
            <Separator />
            <CardContent className="pt-5">
              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="currentPassword" className="text-sm font-medium flex items-center gap-2">
                    <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                    Current Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="currentPassword"
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                      placeholder="Enter your current password"
                      required
                      className="h-10 pr-10 transition-colors focus:border-blue-400"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent text-muted-foreground hover:text-foreground transition-colors"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    >
                      {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="newPassword" className="text-sm font-medium flex items-center gap-2">
                      <KeyRound className="h-3.5 w-3.5 text-muted-foreground" />
                      New Password
                    </Label>
                    <div className="relative">
                      <Input
                        id="newPassword"
                        type={showNewPassword ? 'text' : 'password'}
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                        placeholder="Enter new password"
                        required
                        minLength={6}
                        className="h-10 pr-10 transition-colors focus:border-blue-400"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent text-muted-foreground hover:text-foreground transition-colors"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                      >
                        {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                    <p className="text-[11px] text-muted-foreground">Min. 6 characters</p>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="confirmPassword" className="text-sm font-medium flex items-center gap-2">
                      <CheckCircle2 className="h-3.5 w-3.5 text-muted-foreground" />
                      Confirm Password
                    </Label>
                    <div className="relative">
                      <Input
                        id="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                        placeholder="Confirm new password"
                        required
                        minLength={6}
                        className="h-10 pr-10 transition-colors focus:border-blue-400"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent text-muted-foreground hover:text-foreground transition-colors"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                </div>

                {passwordError && (
                  <Alert variant="destructive" className="animate-in fade-in slide-in-from-top-1">
                    <AlertDescription>{passwordError}</AlertDescription>
                  </Alert>
                )}

                {passwordSuccess && (
                  <Alert className="border-emerald-200 bg-emerald-50 text-emerald-800 animate-in fade-in slide-in-from-top-1">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    <AlertDescription className="text-emerald-700 font-medium">{passwordSuccess}</AlertDescription>
                  </Alert>
                )}

                <Separator className="my-2" />
                <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={handlePasswordCancel} 
                    className="w-full sm:w-auto" 
                    disabled={passwordLoading}
                  >
                    <X className="mr-1.5 h-3.5 w-3.5" />
                    Reset
                  </Button>
                  <Button 
                    type="submit" 
                    className={`w-full sm:w-auto bg-gradient-to-r ${roleColors.bg} hover:opacity-90 text-white shadow-md`} 
                    disabled={passwordLoading}
                  >
                    <Lock className="mr-1.5 h-3.5 w-3.5" />
                    {passwordLoading ? 'Updating...' : 'Update Password'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
